import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useUpdateAccountMutation } from "@/redux/api/accounts.rtkq";
import { profileCompletionSchema, type ProfileCompletionFormValues } from "@/lib/forms/profileCompletionSchema";
import { Building2, Phone, Hash, MapPin } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface ProfileFormProps {
  accountId?: string; // Optional accountId for super admin usage
  initialValues?: Partial<ProfileCompletionFormValues>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  submitButtonText?: string;
}

const DEFAULT_VALUES: ProfileCompletionFormValues = {
  phoneNumber: "",
  businessName: "",
  businessNumber: "",
  city: "",
};

export const ProfileForm: React.FC<ProfileFormProps> = ({
  accountId: propAccountId,
  initialValues,
  onSuccess,
  onError,
  submitButtonText = "שמור שינויים",
}) => {
  const auth = useAuth();

  // Use prop accountId if provided (for super admin usage), otherwise use auth account
  const accountId = propAccountId ?? auth.getAccountId();
  const [updateAccount, { isLoading, isSuccess }] = useUpdateAccountMutation();

  const form = useForm<ProfileCompletionFormValues>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...initialValues,
    },
    mode: "onBlur",
  });

  // Reset form when initialValues change
  useEffect(() => {
    if (initialValues) {
      form.reset({
        ...DEFAULT_VALUES,
        ...initialValues,
      });
    }
  }, [initialValues, form]);

  // Call onSuccess when mutation succeeds (after cache invalidation completes)
  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
    }
  }, [isSuccess, onSuccess]);

  const onSubmit = async (data: ProfileCompletionFormValues) => {
    try {
      if (!accountId) {
        throw new Error("Account ID not found");
      }

      await updateAccount({
        accountId: accountId,
        updates: {
          accountInformation: {
            ...data,
            isProfileComplete: true,
          },
        },
      }).unwrap();
      // onSuccess will be called by useEffect when isSuccess becomes true
    } catch (error) {
      console.error("Error updating profile:", error);
      onError?.(error);
      // Re-throw to keep form in error state and prevent clearing
      throw error;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 text-right">
        {/* Phone Number */}
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                מספר טלפון
              </FormLabel>
              <FormControl>
                <Input placeholder="050-1234567" {...field} dir="ltr" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Business Name */}
        <FormField
          control={form.control}
          name="businessName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                שם העסק
              </FormLabel>
              <FormControl>
                <Input placeholder="שם החברה" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Business Number */}
        <FormField
          control={form.control}
          name="businessNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Hash className="w-4 h-4" />
                מספר עסק
              </FormLabel>
              <FormControl>
                <Input placeholder="123456789" {...field} dir="ltr" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* City */}
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                עיר
              </FormLabel>
              <FormControl>
                <Input placeholder="תל אביב" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-chen-900 hover:bg-chen-800 text-white py-6 text-lg font-semibold mt-6"
        >
          {isLoading ? "שומר..." : submitButtonText}
        </Button>
      </form>
    </Form>
  );
};

export default ProfileForm;
